"use client";

import { useEffect, useState } from "react";
import useSWR from "swr";
import { ProductProcessingUsage } from "@/components/product-processing-usage";
import { MONTHLY_QUOTAS, MONTHLY_QUOTA_KEYS } from "@/constants/quotas";
import { USAGE_ANALYTICS_UNAVAILABLE, USAGE_RESPONSE_INVALID } from "@/lib/usage-messages";
import { getMonthlyPlanUsage } from "@/services/statistics.service";
import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/context/OrganizationContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { PermissionNotice } from "@/components/permission-notice";

const USAGE_REFRESH_INTERVAL_MS = 60_000;

function usageBadgeLabel(limit: number | null, atLimit: boolean) {
  if (limit === 0) return "Disabled";
  if (atLimit) return "Limit reached";
  return "Near Limit";
}

export function ApiUsage() {
  const { user } = useAuth();
  const { can } = useOrganization();
  const canViewUsage = can("analytics");
  const clientId = typeof user?.clientId === "string" ? user.clientId : null;
  const [now, setNow] = useState(() => new Date());

  // Advance the cache key at UTC month rollover, even if polling returns
  // unchanged data. Also refresh the clock when returning to a suspended tab.
  useEffect(() => {
    if (!canViewUsage || !clientId) return;
    const updateClock = () => setNow(new Date());
    updateClock();
    const timer = setInterval(updateClock, USAGE_REFRESH_INTERVAL_MS);
    window.addEventListener("focus", updateClock);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", updateClock);
    };
  }, [canViewUsage, clientId]);

  const month = now.toISOString().slice(0, 7);
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    canViewUsage && clientId ? ["monthly-plan-usage", clientId, month] : null,
    getMonthlyPlanUsage,
    {
      refreshInterval: USAGE_REFRESH_INTERVAL_MS,
      // SWR pauses polling on errors; its default backoff enables automatic recovery.
      onError: (error: unknown) => {
        const cause = error instanceof Error && "cause" in error ? error.cause : undefined;
        // Log only known classifications; network/JSON errors may contain payloads.
        const diagnostic = typeof cause === "string" &&
          (/^HTTP \d{3}$/.test(cause) || cause === USAGE_RESPONSE_INVALID)
          ? cause : "Request or JSON parsing failed";
        console.warn("Monthly plan usage unavailable", { cause: diagnostic });
      },
    },
  );
  // The API returns the first UTC day through today, inclusive. Reject partial
  // or cross-month periods instead of relabeling them as current monthly usage.
  const wrongPeriod = data && (data.period.startDate !== `${month}-01` || data.period.endDate.slice(0, 7) !== month);
  const unavailable = Boolean(error || wrongPeriod);
  const loading = !unavailable && (isLoading || !data);
  const nextMonth = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
  const resetDays = Math.ceil((nextMonth - now.getTime()) / 86_400_000);

  if (!canViewUsage) {
    return (
      <Card>
        <CardContent className="p-6">
          <PermissionNotice feature="analytics" />
        </CardContent>
      </Card>
    );
  }

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p role="status" className="text-sm text-muted-foreground">Usage requires a customer workspace.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-6" aria-busy={loading}>
        {unavailable && <p role="alert" className="text-sm text-red-500">{USAGE_ANALYTICS_UNAVAILABLE}</p>}
        <ProductProcessingUsage value={data?.productProcessing}
          loading={loading} unavailable={unavailable} periodEnd={data?.period.endDate} />
        {MONTHLY_QUOTA_KEYS.map((key) => {
          const config = MONTHLY_QUOTAS[key];
          const name = config.usageLabel;
          // The usage endpoint supplies counts and allowances together. Billing
          // access and separate statistics cannot substitute for an unavailable row.
          const entry = unavailable ? undefined : data?.features[key];

          if (loading || !entry) {
            return (
              <section key={key} className="space-y-2" aria-label={name}>
                <h3 className="text-sm font-medium">{name}</h3>
                {loading ? <Skeleton className="h-5 w-full" /> : (
                  <p
                    role={unavailable ? undefined : "status"}
                    className={`text-sm ${unavailable ? "text-red-500" : "text-muted-foreground"}`}
                  >
                    {name} usage is unavailable.
                  </p>
                )}
              </section>
            );
          }

          const { used, limit } = entry;
          const percentage = limit !== null && limit > 0 ? Math.round((used / limit) * 100) : 0;
          const isAtLimit = limit !== null && used >= limit;
          const isNearLimit = limit !== null && percentage > 80;
          let indicatorColor: string = config.color;
          if (isNearLimit) indicatorColor = "bg-red-500";
          else if (percentage > 60) indicatorColor = "bg-amber-500";

          return (
            <section key={key} className="space-y-2" aria-label={name}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-medium">{name}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm">
                    {used.toLocaleString()} / {limit === null ? "Unlimited" : `${limit.toLocaleString()} calls`}
                  </span>
                  {(isNearLimit || isAtLimit) && (
                    <Badge variant="outline" className="text-amber-500 border-amber-200 bg-amber-50">
                      {usageBadgeLabel(limit, isAtLimit)}
                    </Badge>
                  )}
                </div>
              </div>
              {limit !== null && limit > 0 && (
                <Progress
                  value={Math.min(100, percentage)}
                  aria-label={`${name} usage`}
                  aria-valuetext={`${used.toLocaleString()} of ${limit.toLocaleString()} calls`}
                  className="h-2"
                  indicatorClassName={indicatorColor}
                />
              )}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Reset in {resetDays} day{resetDays === 1 ? "" : "s"}</span>
                {limit !== null && limit > 0 && <span>{percentage}% used</span>}
              </div>
            </section>
          );
        })}

        {unavailable && (
          <Button variant="outline" size="sm" disabled={isValidating} onClick={() => void mutate()}>
            Retry usage
          </Button>
        )}
        <div className="pt-2">
          <p className="text-sm">
            Need more API calls?{" "}
            <a href="/account" className="text-primary font-medium">Upgrade your plan</a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
